import { Table, message, Popover, Button, Typography } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { CheckCircleTwoTone } from "@ant-design/icons";
import EditButton from "../UI/EditButton/editbutton";
import DeleteButton from "../UI/DeleteButton/deletebutton";
import { InputNumber } from "../UI/InputNumberComponent/inputnumber";
import { deleteItemInventoryBeginningItem, editInventoryBeginning } from "../../api/inventoryBeginning";
import { InventoryItem } from "./Interfaces";
import DeleteModal from "./DeleteModal";

const { Text } = Typography;
const mainCurrency = localStorage.getItem("mainCurrency");
const mainCurrencySymbol = mainCurrency? JSON.parse(mainCurrency).symbol : null;

let totalCost = 0;
let totalSales = 0;

type paginationType = { current: number, pageSize: number, total: number, totalPU: number, totalSA : number }

type prop = {
  inventoryBeginningItems: InventoryItem[];
  setInventoryBeginningItems: React.Dispatch<React.SetStateAction<InventoryItem[]>>;
  isLoading: boolean;
  pagination: paginationType;
  handleTableChange: React.Dispatch<any>;
}

const InventoryBeginningList = ({
  inventoryBeginningItems,
  setInventoryBeginningItems,
  pagination,
  handleTableChange,
  isLoading }: prop) => {

  const { t } = useTranslation("common");

  const [selectedEditRow, setSelectedEditRow] = useState<number | undefined>(undefined)
  const [selectedDeleteRow, setSelectedDeleteRow] = useState<number | undefined>(undefined)
  const [editedCost, setEditedCost] = useState<number | null>(null)
  const [editedQuantity, setEditedQuantity] = useState<number | null>(null)
  const [editedSales, setEditedSales] = useState<number | null>(null)
  const [openDeleteModal, setOpenDeleteModal] = useState(false)

  totalCost = pagination.totalPU;
  totalSales = pagination.totalSA;

  const { mutateAsync: editInventoryBeginningMutation, isLoading: editInventoryBeginningMutationLoading } = useMutation(
    "editInventoryBeginning",
    ({ warehouseId, id, editedCost, editedQuantity, editedSale }: { warehouseId: number, id: number, editedCost: number | null, editedQuantity: number | null, editedSale: number | null }) =>
      editInventoryBeginning({ warehouseId, id, editedCost, editedQuantity, editedSale }),
    {
      onSuccess: ({ data }) => {
        message.destroy()
        message.success(data.message)
        setSelectedEditRow(undefined)
        setInventoryBeginningItems(inventoryBeginningItems.map((item: InventoryItem) => {
          if (item.item.id === data.item.id) {
            const { message, ...updatedItem } = data;
            return updatedItem;
          }
          return item;
        }));
      },
      onError: ({ response }) => {
        message.destroy()
        message.error(response ? response.data.message : "Error editing item")
      },
    }
  );

  const { mutateAsync: deleteItemInventoryBeginningItemMutation, isLoading: deleteItemInventoryBeginningItemMutationLoading } = useMutation(
    "deleteItemInventoryBeginningItemMutation",
    ({ warehouseId, id }: { warehouseId: number, id: number | undefined }) =>
      deleteItemInventoryBeginningItem({ warehouseId, id }),
    {
      onSuccess: ({ data }) => {
        message.destroy()
        message.success(data.message)
        setSelectedDeleteRow(undefined)
        setInventoryBeginningItems(inventoryBeginningItems.map((item: InventoryItem) => {
          if (item.item.id === data.item.id) {
            const { message, ...updatedItem } = data;
            return updatedItem;
          }
          return item;
        }));
        setOpenDeleteModal(false)
      },
      onError: ({ response }) => {
        message.destroy()
        message.error(response ? response.data.message : "Error deleting item")
        setOpenDeleteModal(false)
      },
    }
  );

  const HandleEdit = (id: number, cost: any, quantity: any, sale: any) => {
    setSelectedEditRow(id)
    setEditedCost(cost)
    setEditedQuantity(quantity)
    setEditedSales(sale)
  }

  const handleEditSave = (record: InventoryItem, editedCost: number | null, editedQuantity: number | null, editedSales: number | null) => {
    if (record.cost === editedCost && record.quantity === editedQuantity && record.sales === editedSales) {
      setSelectedEditRow(undefined)
      return
    }

    if (!editedQuantity || isNaN(editedQuantity) || parseFloat(editedQuantity.toString()) === 0) {
      message.error('Quantity must be bigger than zero')
      return
    }

    if (!editedCost || isNaN(editedCost) || parseFloat(editedCost.toString()) === 0) {
      message.error('Cost must be bigger than zero')
      return
    }

    if (!editedSales || isNaN(editedSales) || parseFloat(editedSales.toString()) === 0) {
      message.error('Sales must be bigger than zero')
      return
    }    

    editInventoryBeginningMutation({
      warehouseId: 1,
      id: record.item.id,
      editedCost: editedCost,
      editedQuantity: editedQuantity,
      editedSale: editedSales
    })
  }

  const handleDeleteButtonSubmit = async () => {
    await deleteItemInventoryBeginningItemMutation({ warehouseId: 1, id: selectedDeleteRow })
    setSelectedDeleteRow(undefined)
  }

  const columns: {}[] = [
    {
      title: `${t("category")}`,
      dataIndex: "category",
      key: "category",
      width: "10rem",
      render: (category: string, record: InventoryItem) => {
        return <div>{record.item.category.name}</div>
      }
    },
    {
      title: `${t("item-name")}`,
      dataIndex: "name",
      key: "name",
      className: "table-header",
      align: "center",
      render: (name: string, record: InventoryItem) => {
        return <div>{record.item.name}</div>
      }
    },
    {
      title: `${t("barcode")}`,
      dataIndex: "barcode",
      key: "barcode",
      className: "table-header",
      width: "9rem",
      align: "center",
      render: (name: string, record: InventoryItem) => {
        return <div>{record.item.barcode}</div>
      }
    },
    {
      title: `${t("quantity")}`,
      dataIndex: "quantity",
      key: "quantity",
      width: "5rem",
      align: "center",
      render: (quantity: number, record: InventoryItem) => {
        return selectedEditRow === record.item.id ?
          <InputNumber
            maxLength={50}
            style={{ height: '30px', textAlign: "center" }}
            value={editedQuantity?editedQuantity.toString():"0"}
            onChange={(value: number | string | null | undefined) => setEditedQuantity(value === '' ? null : value as number)}
          />
          : <div>{quantity?quantity.toFixed(2):"0.00"}</div>
      }
    },
    {
      title: `${t("cost")}`,
      dataIndex: "cost",
      key: "cost",
      width: "7rem",
      align: "center",
      render: (cost: number, record: InventoryItem) => {
        return selectedEditRow === record.item.id ?
          <InputNumber
            maxLength={50}
            style={{ height: '30px', textAlign: "center"}}
            value={editedCost?editedCost.toString():"0"}
            onChange={(value: number | string | null | undefined) => setEditedCost(value === '' ? null : value as number)}
          />
          : <div>{`${mainCurrencySymbol}${cost?cost.toFixed(2):"0.00"}`}</div>
      }
    },
    {
      title: `${t("sales")}`,
      dataIndex: "sales",
      key: "sales",
      width: "7rem",
      align: "center",
      render: (sale: number, record: InventoryItem) => {
        return selectedEditRow === record.item.id ?
          <InputNumber
            maxLength={50}
            style={{ height: '30px', textAlign: "center"}}
            value={editedSales?editedSales.toString():"0"}
            onChange={(value: number | string | null | undefined) => setEditedSales(value === '' ? null : value as number)}
          />
          : <div>{`${mainCurrencySymbol}${sale?sale.toFixed(2):"0.00"}`}</div>
      }
    },
    {
      title: `${t("totalt-purchase")}`,
      dataIndex: "totalPU",
      key: "totalPU",
      width: "8rem",
      align: "center",
      render: (value: number) => (value ? `${mainCurrencySymbol}${value.toFixed(2)}` : `${mainCurrencySymbol}0.00`),
    }, 
    {
      title: `${t("total-sales")}`,
      dataIndex: "totalSA",
      key: "totalSA",
      width: "8rem",
      align: "center",
      render: (value: number) => (value ? `${mainCurrencySymbol}${value.toFixed(2)}` : `${mainCurrencySymbol}0.00`),
    },    
    {
      title: `${t("profit")}`,
      dataIndex: "profit",
      key: "profit",
      width: "7rem",
      align: "center",
      render: (profit: number, record: InventoryItem) => {
        if (selectedEditRow === record.item.id) {
          let calcProfit = (((Number(editedSales) - Number(editedCost)) / Number(editedCost)) * 100)
          return (
            <div>{`${calcProfit?calcProfit.toFixed(2):"0.00"} %`}</div>
          )
        }
        return <div>{`${record.profitText?record.profitText:"0.00 %"}`}</div>
      }
    },
    {
      title: `${t("action")}`,
      key: "action",
      width: "4rem",
      align: "center" as "center",
      render: (record: InventoryItem) => (
        <>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            {selectedEditRow === record.item.id ? <Popover content={t('confirm-changes')} mouseEnterDelay={0} mouseLeaveDelay={0}>
              <Button
                className="Button"
                type="text"
                icon={<CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: "14px" }} />}
                onClick={() => handleEditSave(record, editedCost, editedQuantity, editedSales)}
                size={"small"}
              />
            </Popover> : <EditButton onClick={() => HandleEdit(record?.item.id, record.cost, record.quantity, record.sales)} />}
            <DeleteButton
              disabled={!record.canDelete}
              onClick={() => {
                setOpenDeleteModal(true)
                setSelectedDeleteRow(record.item.id)
              }}
            />
          </div>

        </>
      ),
    },
  ];

  return (
    <>
      <Table
        scroll={{ x: 1000 }}
        columns={columns}
        dataSource={inventoryBeginningItems}
        rowKey={(record) => record.item.id}
        rowClassName={(record, index) =>
          index % 2 === 0
            ? "table-row-light table-row"
            : "table-row-dark table-row"
        }
        loading={
          isLoading ||
          editInventoryBeginningMutationLoading || deleteItemInventoryBeginningItemMutationLoading}
        pagination={{ ...pagination, showSizeChanger: false }}
        onChange={handleTableChange}

        summary={pageData => {
          if(Math.ceil(pagination.total/pagination.pageSize) === pagination.current) {
           return (
            <>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                <Text type="danger" strong>{t("total")}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}></Table.Summary.Cell>
                <Table.Summary.Cell index={2}></Table.Summary.Cell>
                <Table.Summary.Cell index={3}></Table.Summary.Cell>
                <Table.Summary.Cell index={4}></Table.Summary.Cell>
                <Table.Summary.Cell index={5}></Table.Summary.Cell>
                <Table.Summary.Cell align="center" index={6}>
                <Text type="success" >{`${mainCurrencySymbol}${totalCost ? totalCost.toFixed(2) : "0.00"}`}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell align="center"  index={7}>
                <Text type="success" >{`${mainCurrencySymbol}${totalSales ? totalSales.toFixed(2) : "0.00"}`}</Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </>
          );           
          }
        }}
      />
      <DeleteModal
        open={openDeleteModal} setOpen={setOpenDeleteModal} name={""} onOk={handleDeleteButtonSubmit} isLoading={deleteItemInventoryBeginningItemMutationLoading}
      />
    </>
  );
};

export default InventoryBeginningList;
